import { mediaUnits, tagsEndpoints, AVAILABILITY_CALENDAR_CAPABILITY } from '@ooh/contracts';
import type {
  EndpointDef,
  callEndpoint,
  FilterOptionsResponse,
  MapPinsQuery,
  GetMediaUnitsAvailabilityResponse,
} from '@ooh/contracts';
import {
  mediaUnitAvailabilityQueryKey,
  mediaUnitDetailQueryKey,
  mediaUnitListsQueryKey,
} from '@ooh/screens';
export {
  mapAvailabilityResponseToOccupancy,
  mapAvailabilityResponseToEntriesGrid,
} from '@ooh/screens';
export { suppliersQuery } from '../../../lib/suppliers-query';
import { MEDIA_UNIT_AVAILABILITY_KEY_PREFIX, TAG_LIST_QUERY_KEY } from '../../../lib/query-keys';

// ─── Capability constants ─────────────────────────────────────

export { AVAILABILITY_CALENDAR_CAPABILITY };

// ─── Query key factory ────────────────────────────────────────

export { TAG_LIST_QUERY_KEY } from '../../../lib/query-keys';

export const mediaUnitKeys = {
  all: ['media-units'] as const,
  lists: mediaUnitListsQueryKey,
  list: (sort: object, filters: object) =>
    [...mediaUnitKeys.lists(), JSON.stringify(sort), JSON.stringify(filters)] as const,
  filterOptions: () => [...mediaUnitKeys.all, 'filter-options'] as const,
  detail: mediaUnitDetailQueryKey,
  costHistory: (id: string | number) => ['media-units', String(id), 'cost-history'] as const,
  mapPins: (filters: MapPinsQuery) =>
    [...mediaUnitKeys.all, 'map-pins', JSON.stringify(filters)] as const,
  filterPreset: (campaignId: string | number) =>
    ['campaigns', String(campaignId), 'filter-preset'] as const,
  trash: () => [...mediaUnitKeys.all, 'trash'] as const,
  /** Shared prefix for GET /media-units/availability — see MEDIA_UNIT_AVAILABILITY_KEY_PREFIX. */
  availabilityAll: () => MEDIA_UNIT_AVAILABILITY_KEY_PREFIX,
  availability: mediaUnitAvailabilityQueryKey,
} as const;

// ─── Shared auth interface ────────────────────────────────────

export interface WithCallWithAuth {
  callWithAuth: <TDef extends EndpointDef>(
    def: TDef,
    request: Parameters<typeof callEndpoint<TDef>>[1],
    options?: { signal?: AbortSignal },
  ) => ReturnType<typeof callEndpoint<TDef>>;
}

// ─── Query factories ──────────────────────────────────────────

export function mediaUnitDetailQuery(auth: WithCallWithAuth, id: string | number) {
  return {
    queryKey: mediaUnitKeys.detail(id),
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      auth.callWithAuth(mediaUnits.getById, { id: Number(id) }, { signal }),
  };
}

export { formatsQuery, type MediaFormat } from '../../../lib/media-formats-query';

export function filterOptionsQuery(auth: WithCallWithAuth) {
  return {
    queryKey: mediaUnitKeys.filterOptions(),
    queryFn: ({ signal }: { signal: AbortSignal }): Promise<FilterOptionsResponse> =>
      auth.callWithAuth(mediaUnits.filterOptions, {}, { signal }),
  };
}

/** Active tag definitions for the media-unit tag picker (TagsSection). */
export function tagsPickerQuery(auth: WithCallWithAuth) {
  return {
    queryKey: [...TAG_LIST_QUERY_KEY] as const,
    queryFn: () => auth.callWithAuth(tagsEndpoints.list, {}),
  };
}

export function mapPinsQuery(auth: WithCallWithAuth, filters: MapPinsQuery) {
  return {
    queryKey: mediaUnitKeys.mapPins(filters),
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      auth.callWithAuth(mediaUnits.mapPins, filters, { signal }),
  };
}

export function trashListQuery(auth: WithCallWithAuth) {
  return {
    queryKey: mediaUnitKeys.trash(),
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      auth.callWithAuth(mediaUnits.trashList, {}, { signal }),
  };
}

/**
 * Query factory for GET /media-units/availability.
 *
 * The query is enabled when unitIds is non-empty. Callers are responsible for
 * composing capability guards (e.g. `canViewAvailabilityCalendar`) into the
 * outer `useQuery` `enabled` option. The 404 graceful-degradation catch has
 * been removed — backend enforces the guard authoritatively via FeatureFlagGuard.
 */
export function mediaUnitAvailabilityQuery(
  auth: WithCallWithAuth,
  unitIds: number[],
  from: string,
  to: string,
) {
  return {
    queryKey: mediaUnitKeys.availability(unitIds, from, to),
    queryFn: ({ signal }: { signal: AbortSignal }): Promise<GetMediaUnitsAvailabilityResponse> =>
      auth.callWithAuth(mediaUnits.getAvailability, { unitIds, from, to }, { signal }),
    enabled: unitIds.length > 0,
  };
}

// ─── Shared types ─────────────────────────────────────────────

export type Supplier = { id: number; name: string };
